import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { Box, Breadcrumbs } from "@mui/material";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import * as Yup from "yup";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "../../../components/TextField";
import GridTable from "../../../components/GridTable";
import ActionMenu from "../../../components/ActionMenu";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import { successMessage, errorMessage } from "../../../components/Toaster";
import {
    getInstancesByMachineId,
    createMachineInstance,
    deleteMachineInstance,
} from "./api";
import { MachineInstanceData, CreateMachineInstanceParams, ApiResponse } from "./interface";
import { getErrorMessage } from "../../../utils/error";

const Instance: React.FC = () => {
    const location = useLocation();
    const { machine_id, version_id } = useParams();
    const machine_name = location.state?.machine_name || "Machine";
    const version = location.state?.version || "Version";
    const [instances, setInstances] = useState<MachineInstanceData[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        instanceId: Yup.string().required("Instance ID is required"),
    });

    const columns: GridColDef[] = [
        { field: "machine_instance_id", headerName: "Instance ID", flex: 1 },
        { field: "machine_status", headerName: "Machine Status", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                    ]}
                />
            ),
        },
    ];

    useEffect(() => {
        const fetchInstances = async () => {
            if (!machine_id || !version_id) return;
            setLoading((prev) => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<MachineInstanceData[]> = await getInstancesByMachineId(machine_id, version_id);
                if (res.status && res.data) {
                    setInstances(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch instances.");
                }
            } catch (err) {
                errorMessage(getErrorMessage(err) || "Error fetching instances.");
            } finally {
                setLoading((prev) => ({ ...prev, fetch: false }));
            }
        };

        fetchInstances();
    }, [machine_id, version_id]);

    const openDialog = () => setDialogOpen(true);
    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setDialogOpen(false);
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setItemToDelete(null);
        setDeleteOpen(false);
    };

    const confirmDialog = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        const success = await handleCreate(values);

        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: { instanceId: string }) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            const newInstance: CreateMachineInstanceParams = {
                machine_instance_id: values.instanceId,
                machine_version_id: version_id!,
                machine_id: machine_id!,
            };
            const res = await createMachineInstance(newInstance);
            if (res.status) {
                const newInstance = {
                    machine_instance_id: values.instanceId,
                    machine_id: machine_id!,
                    machine_version_id: version_id!,
                    machine_status: 'READY'
                };
                setInstances((prev) => [...prev, newInstance]);
                successMessage(res.message);
                return true;
            } else {
                errorMessage(res.message || "Failed to create instance.");
                return false;
            }
        } catch (err) {
            errorMessage(getErrorMessage(err) || "Unexpected error.");
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));
        try {
            if (itemToDelete) {
                const res = await deleteMachineInstance(itemToDelete.row.machine_instance_id);
                if (res.status) {
                    setInstances((prev) =>
                        prev.filter((v) => v.machine_instance_id !== itemToDelete.row.machine_instance_id)
                    );
                    successMessage(res.message);
                } else {
                    errorMessage(res.message || "Failed to delete instance");
                }
            }
        } catch (err) {
            errorMessage(getErrorMessage(err) || "Unexpected error");
        } finally {
            setLoading((prev) => ({ ...prev, delete: false }));
            setDeleteOpen(false);
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box>
                    <main className="m-a-20">
                        <div
                            style={{ display: "flex", alignItems: "center", marginBottom: "30px" }}
                        >
                            <Breadcrumbs
                                separator={<NavigateNextIcon fontSize="small" />}
                                aria-label="breadcrumb"
                                style={{ fontSize: "18px", fontWeight: "bold" }}
                            >
                                <Link color="primary" to="/machines">
                                    Machines
                                </Link>
                                <Link color="primary" to={`/machines`}>{machine_name}</Link>
                                <Link color="primary" to={`/machines/${machine_id}/versions`}>
                                    Versions
                                </Link>
                                <Link color="primary" to={`/machines/${machine_id}/versions`}>{version}</Link>
                                <Link color="primary" to={`/machines/${machine_id}/versions/${version_id}/instances`} state={{ machine_name: machine_name, version: version }}>Instances</Link>
                            </Breadcrumbs>
                        </div>
                        <div className="d-flex justify-end">
                            <div className="d-flex justify-between w-100">
                                <TextField
                                    className="w-25"
                                    label="Search"
                                    variant="outlined"
                                    value={searchQuery}
                                    size="small"
                                    startIcon={<SearchIcon />}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <Button
                                    label="Add"
                                    variant="contained"
                                    color="primary"
                                    onClick={openDialog}
                                />
                            </div>
                        </div>
                        <div className="ag-theme-alpine m-t-20" style={{ height: "100%", width: "100%" }}>
                            <GridTable
                                rows={instances}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.machine_instance_id}
                            />
                        </div>
                        <Formik
                            initialValues={{
                                instanceId: "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({ values, errors, touched, handleChange, handleSubmit, resetForm }) => (
                                <Dialog
                                    open={isDialogOpen}
                                    onClose={() => closeDialog(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={"Create Instance"}
                                    confirmButtonText={"Add"}
                                    cancelButtonText="Cancel"
                                    icon={<PrecisionManufacturingIcon />}
                                    message={
                                        <Box padding={4}>
                                            {loading.create ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={12}>
                                                            <TextField
                                                                label="Instance ID"
                                                                name="instanceId"
                                                                value={values.instanceId}
                                                                onChange={handleChange}
                                                                error={Boolean(errors.instanceId && touched.instanceId)}
                                                                helperText={touched.instanceId && errors.instanceId}
                                                                required
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                        <Dialog
                            open={deleteOpen}
                            onClose={handleDeleteCancel}
                            onConfirm={handleDeleteConfirm}
                            title={!loading.delete ? "Delete Instance" : ""}
                            confirmButtonText={!loading.delete ? "Confirm" : ""}
                            cancelButtonText={!loading.delete ? "Cancel" : ""}
                            icon={<DeleteIcon />}
                            message={
                                <Box padding={2}>
                                    {loading.delete ? (
                                        <Loader color="primary" size={40} />
                                    ) : (
                                        "Are you sure you want to delete this Instance?"
                                    )}
                                </Box>
                            }
                        />
                    </main>
                </Box>
            )}
        </>
    );
};

export default Instance;
